import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import { loginChild, registerChild, login, registerAdult, fetchCoins } from "../hooks/api";
import { storageService } from "../services/storageService";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Verificar primero si hay un usuario niño
    const storedChildUser = localStorage.getItem("childUser");
    if (storedChildUser) {
      try {
        setUser(JSON.parse(storedChildUser));
        setIsLoading(false);
        return;
      } catch {
        localStorage.removeItem("childUser");
        localStorage.removeItem("childId");
      }
    }

    // Si no hay niño, verificar si hay un usuario adulto
    const storedAdultUser = localStorage.getItem("adultUser");
    if (storedAdultUser) {
      try {
        const adultData = JSON.parse(storedAdultUser);
        setUser({
          ...adultData,
          rol: 'adult'
        });
      } catch {
        localStorage.removeItem("adultUser");
        localStorage.removeItem("adultId");
      }
    }

    setIsLoading(false);
  }, []);

  const handleRegisterChild = useCallback(
    async (childData) => {
      setIsLoading(true);
      setError(null);

      try {
        // Get the old child_id before removing it, so we can clean up their specific data
        const oldChildId = localStorage.getItem("childId");

        localStorage.removeItem("childId");
        localStorage.removeItem("childUser");
        localStorage.removeItem("childNombre");
        localStorage.removeItem("childEdad");
        localStorage.removeItem("childAvatar");
        localStorage.removeItem("avatarData");
        localStorage.removeItem("childRoom");
        localStorage.removeItem("selectedCountries"); // Generic key fallback

        // Remove old child's specific country data if it exists
        if (oldChildId) {
          localStorage.removeItem(`selectedCountries_${oldChildId}`);
        }

        const response = await registerChild(childData);

        if (response.success) {
          // Crear un avatar por defecto válido usando el género seleccionado
          const selectedGender = childData.genero || "boy";
          const defaultAvatar = {
            hair: 0,
            mouth: 0,
            shirt: 0,
            gender: selectedGender,
            pants: 0,
            name: childData.nombre,
            genderId: selectedGender === "girl" ? 1 : 0,
            skin: 0,
            eyes: 0,
            shoes: 0,
          };

          storageService.saveChildData({
            ...childData,
            child_id: response.child_id,
            avatar: defaultAvatar,
          });

          const childUser = {
            id: response.child_id,
            nombre: childData.nombre,
            rol: "child",
            child_id: response.child_id,
            avatar: defaultAvatar,
            recovered: childData.recovered || false,
          };

          localStorage.setItem("childId", response.child_id);
          localStorage.setItem("childUser", JSON.stringify(childUser));
          localStorage.setItem("childNombre", childData.nombre);
          localStorage.setItem("childAvatar", JSON.stringify(defaultAvatar));
          localStorage.setItem("avatarData", JSON.stringify(defaultAvatar));
          const initialRoom = { wallIndex: 0, itemsArray: [] };
          localStorage.setItem("childRoom", JSON.stringify(initialRoom));
          // Initialize empty countries array for the new child
          localStorage.setItem(`selectedCountries_${response.child_id}`, JSON.stringify([]));

          // Marcar que es un usuario nuevo para mostrar las intros
          localStorage.setItem('showIntroOnWorldMap', 'true');
          localStorage.setItem('showWelcomeOnMenu', 'true');

          // Actualizar el estado del usuario para que quede autenticado inmediatamente
          setUser(childUser);

          // Disparar evento personalizado para forzar actualización de contextos (Room y Country)
          window.dispatchEvent(new StorageEvent('storage', {
            key: 'childId',
            newValue: response.child_id,
            oldValue: oldChildId,
            storageArea: localStorage
          }));

          // NO vincular automáticamente - dejar que la pantalla de confirmación lo maneje
          // El token de invitación permanece en localStorage para que InvitationConfirmation pueda usarlo
          const invitationToken = localStorage.getItem('invitationToken');
          console.log('Checking for invitation token after registration:', invitationToken ? 'FOUND' : 'NOT FOUND');

          return { success: true, childId: response.child_id, needsInvitationConfirmation: !!invitationToken };
        }
        return response;
      } catch (err) {
        setError(err.message || "Error en el registro del niño");
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const logout = useCallback(() => {
    storageService.logout();
    setUser(null);
  }, []);

  const handleLoginChild = useCallback(async (nombre) => {
    setIsLoading(true);
    setError(null);

    try {
      // Intentar con diferentes variaciones del nombre para compatibilidad case-insensitive
      const variaciones = [
        nombre,                                    // Original
        nombre.toLowerCase(),                      // minúsculas
        nombre.charAt(0).toUpperCase() + nombre.slice(1).toLowerCase(), // Primera mayúscula
        nombre.toUpperCase(),                      // MAYÚSCULAS
      ];

      // Eliminar duplicados
      const variacionesUnicas = [...new Set(variaciones)];

      let data = null;
      let ultimoError = null;

      // Intentar con cada variación hasta que una funcione
      for (const variacion of variacionesUnicas) {
        try {
          data = await loginChild(variacion);
          if (data.child_id) {
            break; // Si encontramos el usuario, salimos del loop
          }
        } catch (err) {
          ultimoError = err;
          // Continuar con la siguiente variación
        }
      }

      // Si ninguna variación funcionó, lanzar el último error
      if (!data || !data.child_id) {
        throw ultimoError || new Error("Usuario no encontrado");
      }

      // Guardar nuevos datos
      storageService.saveChildData(data);

      // *** NUEVO: guarda en localStorage los datos del cuarto que trae el backend ***
      const roomFromServer = {
        wallIndex: data.wall_index ?? 0,
        itemsArray: data.room_items ?? [],
      };
      localStorage.setItem("childRoom", JSON.stringify(roomFromServer));

      // Guarda datos del cuarto del diario que trae el backend — siempre, aunque venga vacío,
      // para no arrastrar la decoración cacheada de otro niño logueado antes en este dispositivo
      const diaryRoomFromServer = {
        itemsArray:    data.diary_items          ?? [],
        itemPositions: data.diary_item_positions ?? {},
      };
      localStorage.setItem("childDiaryRoom", JSON.stringify(diaryRoomFromServer));

      // Guarda datos del cuarto de emociones que trae el backend — mismo criterio que arriba
      const emotionRoomFromServer = {
        itemsArray:    data.emotion_items          ?? [],
        itemPositions: data.emotion_item_positions ?? {},
      };
      localStorage.setItem("childEmotionRoom", JSON.stringify(emotionRoomFromServer));

      // *** NUEVO: guarda los países seleccionados que vienen del backend ***
      if (data.selected_countries && Array.isArray(data.selected_countries)) {
        const countryKey = `selectedCountries_${data.child_id}`;
        localStorage.setItem(countryKey, JSON.stringify(data.selected_countries));
      }

      // Usar el avatar que viene del servidor
      const childAvatar = data.avatar || {
        hair: 0,
        mouth: 0,
        shirt: 0,
        gender: "boy",
        pants: 0,
        name: data.nombre || nombre,
        genderId: 0,
        skin: 0,
        eyes: 0,
        shoes: 0,
      };

      const childUser = {
        id: data.child_id,
        nombre: data.nombre || nombre,
        rol: "child",
        child_id: data.child_id,
        avatar: childAvatar,
        recovered: data.recovered || false,
        language: data.language || null,
        adulto_de_confianza: data.adulto_de_confianza || { nombre: '', mail: '' },
        es_howemaker: data.es_howemaker || false,
      };

      localStorage.setItem("childId", data.child_id);
      localStorage.setItem("childUser", JSON.stringify(childUser));
      localStorage.setItem("childNombre", data.nombre || nombre);
      localStorage.setItem("childAvatar", JSON.stringify(childAvatar));
      localStorage.setItem("avatarData", JSON.stringify(childAvatar));

      if (data.edad) localStorage.setItem("childEdad", data.edad);

      // Recuperar saldo de HoweCoins
      fetchCoins(data.child_id).then(res => {
        localStorage.setItem("howeCoins", String(res.howe_coins ?? 0));
      }).catch(() => {
        localStorage.setItem("howeCoins", "0");
      });

      setUser(childUser);

      // NO vincular automáticamente - dejar que la pantalla de confirmación lo maneje
      const invitationToken = localStorage.getItem('invitationToken');
      console.log('Checking for invitation token after login:', invitationToken ? 'FOUND' : 'NOT FOUND');

      return { success: true, child: childUser, needsInvitationConfirmation: !!invitationToken };
    } catch (err) {
      setError(err.message || "Error al iniciar sesión");
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Nueva función para aceptar invitación
  const acceptInvitation = useCallback(async (token, childId) => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000/api';
      console.log('Accepting invitation with child_id:', childId);

      const linkResponse = await fetch(`${apiBaseUrl}/adult/link-child-to-group`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          child_id: childId
        })
      });

      const linkData = await linkResponse.json();
      console.log('Link response:', linkResponse.status, linkData);

      if (linkResponse.ok) {
        console.log('Niño vinculado exitosamente al grupo:', linkData.grupo_name);
        localStorage.removeItem('invitationToken');
        return { success: true, data: linkData };
      } else {
        console.error('Error al vincular niño:', linkData.error || linkData.message);
        return { success: false, error: linkData.error || linkData.message };
      }
    } catch (error) {
      console.error('Error al vincular niño con grupo:', error);
      return { success: false, error: error.message };
    }
  }, []);

  // Nueva función para rechazar invitación
  const rejectInvitation = useCallback(async () => {
    try {
      console.log('Rejecting invitation - removing token');
      localStorage.removeItem('invitationToken');
      return { success: true };
    } catch (error) {
      console.error('Error al rechazar invitación:', error);
      return { success: false, error: error.message };
    }
  }, []);

  const handleLoginAdult = useCallback(async (email, password) => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await login(email, password);

      if (!data.accessToken) {
        throw new Error("No se recibió token de acceso");
      }

      // Guardar tokens
      localStorage.setItem("accessToken", data.accessToken);
      localStorage.setItem("refreshToken", data.refreshToken || "");
      localStorage.setItem("idToken", data.idToken || "");

      // Detectar rol (admin o adult) - viene del backend
      const userRole = data.role || "adult";

      // Crear objeto de usuario adulto o admin
      const adultUser = {
        id: data.adult_id || email,
        email: email,
        rol: userRole,
        adult_id: data.adult_id,
        nombre: data.nombre || "",
        apellido: data.apellido || "",
        language: data.language || null,
      };

      localStorage.setItem("adultId", data.adult_id);
      localStorage.setItem("adultUser", JSON.stringify(adultUser));
      localStorage.setItem("userRole", userRole);

      setUser(adultUser);

      return { success: true, adult: adultUser };
    } catch (err) {
      setError(err.message || "Error al iniciar sesión");
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleRegisterAdult = useCallback(async (adultData) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await registerAdult(adultData);

      if (response.success || response.adult_id) {
        const adultUser = {
          id: response.adult_id,
          email: adultData.email,
          rol: "adult",
          adult_id: response.adult_id,
          nombre: adultData.nombre || response.nombre || '',
          apellido: adultData.apellido || response.apellido || '',
          pais: adultData.pais || '',
          estado: adultData.estado || '',
          approved: response.approved || false,
        };

        // Guardar tokens si vienen en la respuesta
        if (response.accessToken) {
          localStorage.setItem("accessToken", response.accessToken);
        }
        if (response.refreshToken) {
          localStorage.setItem("refreshToken", response.refreshToken);
        }
        if (response.idToken) {
          localStorage.setItem("idToken", response.idToken);
        }

        localStorage.setItem("adultId", response.adult_id);
        localStorage.setItem("adultUser", JSON.stringify(adultUser));

        setUser(adultUser);

        return { success: true, adultId: response.adult_id };
      }

      return response;
    } catch (err) {
      setError(err.message || "Error en el registro del adulto");
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      user,
      isLoading,
      error,
      loginChild: handleLoginChild,
      registerChild: handleRegisterChild,
      loginAdult: handleLoginAdult,
      registerAdult: handleRegisterAdult,
      acceptInvitation,
      rejectInvitation,
      logout,
      setError,
    }),
    [user, isLoading, error, handleLoginChild, handleRegisterChild, handleLoginAdult, handleRegisterAdult, acceptInvitation, rejectInvitation, logout]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe usarse dentro de un AuthProvider");
  }
  return context;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
